(ns manage.adset-preview-panel.db.templates
  (:require [clojure.set :refer [difference rename-keys]]
            [common.db.interceptors :refer [forwarded-event]]
            [common.util :as util]
            [common.widgets.iframe.db :as iframe]
            [re-frame.core :as rf]))

(def initial-state
  {:iframes {}
   :auto-replay? true
   :context nil
   :templates []
   :visible-template-ids #{}
   :zoom-level 100
   :masonry-instance nil})

(defn revision->template
  "Transform revision into template"
  [{:keys [base-url width height template template-type]}]
  #:template {:id (:id template)
              :url (str base-url "/index.html?_lemonpiPreview")
              :width width
              :height height
              :template-type template-type
              :name (:name template)})

(defn templates->template-ids
  "Convert templates to a set of ids"
  [templates]
  (->> templates (map :template/id) set))

(defn template-ids-not-included-in
  "Return a set of template ids where template id is not present in `excluded-ids`."
  [templates excluded-ids]
  (difference (templates->template-ids templates)
              excluded-ids))

(rf/reg-fx :adset-preview.templates/masonry-layout
  (fn [^js/Masonry instance]
    (when instance
      (letfn [(render [] (.layout instance))]
        (.requestAnimationFrame js/window render)))))

(defn local-template-id->template
  [id width height]
  {:template/url (str "http://127.0.0.1:4005/" id "/concept.html?_lemonpiPreview")
   :template/id id
   :template/name id
   :template/width (int width)
   :template/height (int height)})

(rf/reg-event-fx :adset-preview.templates/init
  [(rf/inject-cofx :query-params)]
  (fn [{:keys [db]
        {:keys [template-id width height]} :query-params} _]
    (let [template (when template-id (local-template-id->template template-id width height))
          initial-state* (assoc initial-state
                                :external-template template)]
      {:db (assoc db :adset-preview.templates initial-state*)
       :forward-events [{:register :adset-preview.templates/set-and-evaluate-templates-context
                         :events #{:adset-preview.variants/set-selected-context}
                         :dispatch-to [:adset-preview.templates/set-and-evaluate-templates-context]}
                        {:register :evaluate-context-success
                         :events #{:resource.adset-preview/evaluate-success}
                         :dispatch-to [:adset-preview.templates/evaluate-context-success]}]})))

(rf/reg-event-fx :adset-preview.templates/evaluate-context-success
  [forwarded-event (rf/inject-cofx :namespace-values :adset-preview.templates)]
  (fn [{:adset-preview.templates/keys [visible-template-ids templates]}
       [_ {:keys [creative]} content-object]]
    (let [iframe-ids (->> templates
                          (filter #(visible-template-ids (:template/id %)))
                          (map #(rename-keys % {:template/id :id
                                                :template/width :width
                                                :template/height :height}))
                          (iframe/dimensions->template-ids creative))]
      {:dispatch [:iframe/post-content-object
                  {:ids iframe-ids
                   :content-object content-object}]})))

(rf/reg-event-fx :adset-preview.templates/destroy
  (fn [{:keys [db]} _]
    {:db (dissoc db :adset-preview.templates)
     :forward-events [{:unregister :set-and-evaluate-templates-context}
                      {:unregister :evaluate-context-success}]}))

(rf/reg-event-fx :adset-preview.templates/set-and-evaluate-templates-context
  [forwarded-event]
  (fn [{:keys [db]} [_ context]]
    {:db (assoc-in db [:adset-preview.templates :context] context)
     :dispatch [:adset-preview.templates/evaluate-templates-context]}))

(rf/reg-event-fx :adset-preview.templates/evaluate-templates-context
  [(rf/inject-cofx :namespace-values :adset-preview.templates)
   (rf/inject-cofx :sub :adset-preview.variants/use-request-context?)]
  (fn [{:adset-preview.templates/keys [context templates auto-replay? visible-template-ids]
        :adset-preview.variants/keys [use-request-context?]} _]
    (let [opts {:add-request-context (boolean use-request-context?)}
          template->dispatch (fn [{:template/keys [id]}]
                               [:adset-preview.templates/evaluate-template-context context id opts])]
      (cond-> {}
        (false? auto-replay?) (assoc :dispatch-n (mapv template->dispatch templates))
        auto-replay? (assoc :dispatch [:iframe/reload visible-template-ids])))))

(rf/reg-event-fx :adset-preview.templates/evaluate-template-context
  [(rf/inject-cofx :namespace-values :adset-preview.templates)
   (rf/inject-cofx :namespace-values :adset-preview.core)
   (rf/inject-cofx :sub [:resource.content-function-version/active-version {:only-staged true}])
   (rf/inject-cofx :sub :adset-preview.products/products-for-selectors)]
  (fn [{:adset-preview.templates/keys [templates]
        :resource.adset-preview/keys [content-function]
        :adset-preview.products/keys [products-for-selectors]
        :resource.content-function-version/keys [active-version]}
       [_ context template-id {:keys [add-request-context]
                               :or {add-request-context false}}]]
    (let [[{:template/keys [width height]}] (filter (comp #{template-id} :template/id) templates)
          products-for-selectors* (reduce-kv (fn [acc k {:keys [count]}]
                                               (if count
                                                 (assoc-in acc [k :count] count)
                                                 acc))
                                             products-for-selectors context)]
      {:dispatch [:resource.adset-preview/evaluate
                  (cond-> {:content-function content-function
                           :context (util/filter-nils-map context :remove-empty-maps? true)
                           :add-request-context add-request-context
                           :width width
                           :height height}
                    (some #(seq (:items (second %))) products-for-selectors*) (assoc :products products-for-selectors*))
                  (:name active-version)]})))

(rf/reg-event-fx :adset-preview.templates/set-templates
  [(rf/inject-cofx :namespace-values :adset-preview.templates)]
  (fn [{:keys [db]
        :adset-preview.templates/keys [templates visible-template-ids]}
       [_ fetched-templates]]
    (let [hidden-template-ids (template-ids-not-included-in templates
                                                            visible-template-ids)]
      {:db (update db :adset-preview.templates assoc
                   :templates fetched-templates
                   :visible-template-ids (template-ids-not-included-in
                                          fetched-templates
                                          hidden-template-ids))})))

(rf/reg-event-fx :adset-preview.templates/add-templates
  [(rf/inject-cofx :namespace-values :adset-preview.templates)]
  (fn [{:keys [db]
        :adset-preview.templates/keys [templates visible-template-ids]}
       [_ {:template/keys [id] :as new-template}]]
    {:db (update db :adset-preview.templates assoc
                 :templates (conj templates new-template)
                 :visible-template-ids (set (conj visible-template-ids id)))}))

(rf/reg-event-fx :adset-preview.templates/show
  (fn [{:keys [db]} [_ id]]
    {:db (update-in db [:adset-preview.templates :visible-template-ids] conj id)
     ;; reload template after they have entered the DOM
     :fx [[:dispatch-later {:ms 100 :dispatch [:iframe/reload [id]]}]]}))

(rf/reg-event-db :adset-preview.templates/hide
  (fn [db [_ id]]
    (update-in db [:adset-preview.templates :visible-template-ids] disj id)))

(rf/reg-event-db :adset-preview.templates/zoom
  (fn [db, [_ zoom-level]]
    (assoc-in db [:adset-preview.templates :zoom-level] zoom-level)))

(rf/reg-event-fx :adset-preview.templates/iframe-ready
  [(rf/inject-cofx :namespace-values :adset-preview.templates)
   (rf/inject-cofx :sub :adset-preview.variants/use-request-context?)]
  (fn [{:adset-preview.templates/keys [context]
        :adset-preview.variants/keys [use-request-context?]} [_ id]]
    (when context
      {:dispatch [:adset-preview.templates/evaluate-template-context context id
                  {:add-request-context (boolean use-request-context?)}]})))

(rf/reg-event-fx :adset-preview.templates/show-all
  (fn [{:keys [db]} _]
    (let [{:keys [templates visible-template-ids]} (:adset-preview.templates db)
          hidden-template-ids (difference (set (map :template/id templates)) visible-template-ids)]
      {:db (assoc-in db [:adset-preview.templates :visible-template-ids]
                     (templates->template-ids templates))
       ;; reload templates after they have entered the DOM
       :fx [[:dispatch-later {:ms 100 :dispatch [:iframe/reload hidden-template-ids]}]]})))

(rf/reg-event-db :adset-preview.templates/hide-all
  (fn [db _]
    (update-in db [:adset-preview.templates :visible-template-ids] empty)))

(rf/reg-event-fx :adset-preview.templates/replay-all
  [(rf/inject-cofx :namespace-values :adset-preview.templates)]
  (fn [{:adset-preview.templates/keys [visible-template-ids]} _]
    {:dispatch [:iframe/reload visible-template-ids]}))

(rf/reg-event-db :adset-preview.templates/register-masonry-instance
  (fn [db [_ instance]]
    (assoc-in db [:adset-preview.templates :masonry-instance] instance)))

(rf/reg-event-fx :adset-preview.templates/render-masonry-layout
  [(rf/inject-cofx :namespace-values :adset-preview.templates)]
  (fn [{:adset-preview.templates/keys [masonry-instance]} _]
    {:adset-preview.templates/masonry-layout masonry-instance}))

(rf/reg-event-db :adset-preview.templates/toggle-auto-replay
  (fn [db [_ enabled?]]
    (assoc-in db [:adset-preview.templates :auto-replay?] enabled?)))

(rf/reg-sub :adset-preview.templates/value
  (fn [db [_ path default]]
    (get-in db [:adset-preview.templates path] default)))

(rf/reg-sub :adset-preview.templates/visible-templates
  :<- [:adset-preview.templates/value :templates []]
  :<- [:adset-preview.templates/value :visible-template-ids #{}]
  (fn [[templates visible-template-ids] _]
    (filter (comp visible-template-ids :template/id) templates)))

(rf/reg-sub :adset-preview.templates/has-templates?
  :<- [:adset-preview.templates/value :templates []]
  (fn [templates _]
    (pos? (count templates))))

(rf/reg-sub :adset-preview.templates/visible?
  :<- [:adset-preview.templates/value :visible-template-ids]
  (fn [visible-template-ids [_ id]]
    (boolean (visible-template-ids id))))
